import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Code,
  Link as LinkIcon,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

interface EditorBubbleMenuProps {
  editor: Editor;
}

export function EditorBubbleMenu({ editor }: EditorBubbleMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateMenu = () => {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (!hasSelection) {
        setShowMenu(false);
        return;
      }

      setShowMenu(true);

      // Get selection coordinates
      const start = editor.view.coordsAtPos(from);
      const end = editor.view.coordsAtPos(to);

      // Position menu above selection
      const left = (start.left + end.left) / 2;
      const top = start.top;

      setMenuPosition({
        top: top - 50,
        left: left,
      });
    };

    editor.on('selectionUpdate', updateMenu);
    editor.on('transaction', updateMenu);

    return () => {
      editor.off('selectionUpdate', updateMenu);
      editor.off('transaction', updateMenu);
    };
  }, [editor]);

  const handleSetLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkUrl('');
    setShowLinkInput(false);
  };

  if (!showMenu) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50"
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        transform: 'translateX(-50%)',
      }}
    >
      <div className="flex items-center divide-x divide-border">
        {/* Text Formatting */}
        <div className="flex items-center p-1">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('bold') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('italic') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('underline') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('code') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Code"
          >
            <Code className="w-4 h-4" />
          </button>
        </div>

        {/* Headings */}
        <div className="flex items-center p-1">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('heading', { level: 1 }) ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('heading', { level: 2 }) ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
        </div>

        {/* Lists & Quote */}
        <div className="flex items-center p-1">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('bulletList') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('orderedList') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`p-2 rounded hover:bg-accent transition-colors ${
              editor.isActive('blockquote') ? 'bg-accent text-primary' : 'text-muted-foreground'
            }`}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </button>
        </div>

        {/* Link */}
        <div className="flex items-center p-1">
          {!showLinkInput ? (
            <button
              onClick={() => {
                const previousUrl = editor.getAttributes('link').href;
                setLinkUrl(previousUrl || '');
                setShowLinkInput(true);
              }}
              className={`p-2 rounded hover:bg-accent transition-colors ${
                editor.isActive('link') ? 'bg-accent text-primary' : 'text-muted-foreground'
              }`}
              title="Add Link"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-1 px-2">
              <input
                type="url"
                placeholder="Enter URL..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSetLink();
                  }
                  if (e.key === 'Escape') {
                    setShowLinkInput(false);
                  }
                }}
                className="w-48 px-2 py-1 text-sm bg-background border border-border rounded"
                autoFocus
              />
              <button
                onClick={handleSetLink}
                className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Set
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
